import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import _ from "lodash";
import axios from "axios";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import { useTheme } from "@mui/styles";
import useGetPagePermission from "hooks/useGetPagePermission";
import { apiUrl } from "features/configure";
import { MetaCRMTag } from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import IntegrationSettings from "./IntegrationSettings";
import BasicModal from "features/metadesk/chat/BasicModal";
import { Checkbox } from "@metacrm/metacrm-material-ui/dist/Checkbox";
import { Loading } from "features/common";
import { Button, SIZE, COLOR } from "@metacrm/metacrm-material-ui/dist/Button";
import {
  StyledNotifConfigContainer,
  StyledIntegrationWrapper,
} from "./IntegrationNotification.styles";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useNavigateEntity from "hooks/useNavigateEntity";

const IntegrationTelegramNotification = ({ isSetting = false }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { navigateEntity } = useNavigateEntity();
  const pagePermission = useGetPagePermission();
  const dispatchSnackbar = useDispatchSnackbar();
  const { canEdit } = pagePermission("integration");
  const [entityName, isCustomDomain] = useOutletContext();

  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [telegramNotifBotConfig, setTelegramNotifBotConfig] = useState({});
  const [defaultEvents, setDefaultEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const actionMapping = {
    ticketcreated: formatMessage({
      id: "telegramNotificationSettings.event.ticketCreate",
    }),
    messagereceived: formatMessage({
      id: "telegramNotificationSettings.event.newMessage",
    }),
    ticketclosed: formatMessage({
      id: "telegramNotificationSettings.event.ticketClose",
    }),
    ticketAiReceived: formatMessage({
      id: "global.notification.newAITicketReceived",
    }),
    ticketAiCompleted: formatMessage({
      id: "global.notification.AITicketCompleted",
    }),
    ticketAiToHuman: formatMessage({
      id: "global.notification.AITicketTransferred",
    }),
  };

  const groupName = _.get(telegramNotifBotConfig, "config.groupName", "");
  const events = _.get(telegramNotifBotConfig, "config.events", []);

  const isValidToSave = () => {
    // order of elements should not matter for checking equality
    return (
      !_.isEqual([...selectedEvents].sort(), [...events].sort()) &&
      selectedEvents.length > 0
    );
  };

  useEffect(async () => {
    await getTelegramNotifConfig();
  }, []);

  useEffect(() => {
    setSelectedEvents(events);
  }, [telegramNotifBotConfig]);

  const handleOpenEditModal = () => {
    setOpenModal(true);
  };

  const fallbackRoute = () => {
    navigateEntity("/settings/general/integration");
  };

  const getTelegramNotifConfig = async () => {
    try {
      setLoading(true);
      const [eventResult, dataResult] = await Promise.all([
        await axios.get(apiUrl + `/api/webhook/${entityName}/events`),
        await axios.get(
          apiUrl + `/api/telegram/notification/${entityName}/config`
        ),
      ]);
      setDefaultEvents(eventResult.data);
      setTelegramNotifBotConfig(dataResult.data);
    } catch (err) {
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.gettingConfig" }),
        variant: "error",
      });
      fallbackRoute();
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await axios.delete(
        apiUrl + `/api/telegram/notification/${entityName}/config`
      );
      setTelegramNotifBotConfig({});
      fallbackRoute();
    } catch (err) {
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.disconnecting" }),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        apiUrl + `/api/telegram/notification/${entityName}/config`,
        {
          events: selectedEvents,
        }
      );
      setTelegramNotifBotConfig(data);
      setOpenModal(false);
    } catch (err) {
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.saving" }),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderTelegramNotificationConfigItem = () => {
    return (
      <div style={{ marginRight: 40 }}>
        <div
          className="fade-up"
          style={{
            color: theme.customColors.grey[500],
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {formatMessage({
            id: "telegramNotificationSettings.location.subtitle",
          })}
        </div>
        <div className="betweenRow fade-up-more" style={{ marginBottom: 12 }}>
          <StyledNotifConfigContainer className="betweenRow">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div>
                <div
                  style={{
                    marginBottom: 16,
                    color: theme.customColors.grey[800],
                    fontSize: 14,
                    fontWeight: theme.fontWeight.MEDIUM,
                  }}
                >
                  {groupName}
                </div>
                <Box display={"flex"} alignItems={"center"} columnGap={"4px"}>
                  <div className="label" style={{ marginLeft: 0 }}>
                    {formatMessage({
                      id: "telegramNotificationSettings.event.subtitle",
                    })}
                  </div>
                  <div className="startRow">
                    {!events.length && "-"}
                    {_.map(events, (eId, i) => {
                      const matchEvent = _.find(
                        defaultEvents,
                        (de) => de._id === eId
                      );
                      const keyName = matchEvent.model + matchEvent.action;
                      return (
                        <MetaCRMTag
                          key={i}
                          option={{ label: actionMapping[keyName] }}
                          style={{
                            marginRight: 10,
                            borderRadius: 12,
                            padding: "2px 6px",
                            color: theme.customColors.grey[600],
                          }}
                        />
                      );
                    })}
                  </div>
                </Box>
              </div>
              {canEdit && (
                <i
                  className="meta-crm-icon-ic_edit font-size-18"
                  onClick={handleOpenEditModal}
                  style={{ cursor: "pointer" }}
                />
              )}
            </Box>
          </StyledNotifConfigContainer>
        </div>
        <div style={{ color: theme.customColors.grey[500], fontSize: 14 }}>
          {formatMessage({
            id: "integration.telegramNotificationSettings.hint",
          })}
        </div>
      </div>
    );
  };

  const renderTelegramNotificationModal = () => {
    return (
      <BasicModal
        open={openModal}
        mode="action"
        textAlign="left"
        buttonWidth={"180px"}
        onClose={() => {
          setOpenModal(false);
        }}
        confirmDisabled={!isValidToSave() || loading}
        modalTitle={formatMessage({
          id: "telegramNotificationSettings.popup.title",
        })}
        modalContent={
          <div>
            <Box className="subTitle" mb="8px" mt="20px">
              {formatMessage({
                id: "telegramNotificationSettings.popup.location",
              })}
            </Box>
            <Box
              sx={{
                border: `1px solid ${theme.customColors.grey[300]}`,
                color: theme.customColors.grey[500],
                padding: "12px 16px",
                borderRadius: "3px",
                background: theme.customColors.grey[100],
                fontSize: "14px",
              }}
            >
              {groupName}
            </Box>
            <div className="hr" />
            <Box className="subTitle" mb="8px">
              {formatMessage({
                id: "telegramNotificationSettings.popup.actions",
              })}
            </Box>
            <Box className="description" mb="12px">
              {formatMessage({
                id: "telegramNotificationSettings.popup.actions.description",
              })}
            </Box>
            {_.map(defaultEvents, (actionEvent) => {
              if (!actionEvent) return;
              const actionKey = actionEvent.model + actionEvent.action;
              return (
                <Box mb="12px" key={actionKey}>
                  <Checkbox
                    name="events"
                    onChange={(e) => {
                      const checked = e.target.checked;
                      let newActions = [...selectedEvents];
                      if (checked) {
                        newActions.push(actionEvent._id);
                      } else {
                        newActions = _.filter(
                          newActions,
                          (item) => item !== actionEvent._id
                        );
                      }
                      setSelectedEvents(newActions);
                    }}
                    label={actionMapping[actionKey]}
                    checked={_.includes(selectedEvents, actionEvent._id)}
                    mr={5}
                  />
                </Box>
              );
            })}

            <Box className="hr" mb="18px" />
          </div>
        }
        confirmText={loading ? "Saving..." : "Save"}
        onHandleConfirm={handleSave}
      />
    );
  };

  return (
    <>
      {renderTelegramNotificationModal()}
      <StyledIntegrationWrapper isSetting={isSetting}>
        <IntegrationSettings
          maxWidth={622}
          controlHeaderView={true}
          headerContent={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="lgTitle" style={{ marginTop: 18 }}>
                {formatMessage({ id: "integration.telegramNotification.name" })}
              </div>
              <div>
                <Button
                  color={COLOR.SECONDARY}
                  size={SIZE.XL}
                  onClick={handleDisconnect}
                  style={{ width: 120, height: 36 }}
                >
                  {formatMessage({ id: "global.btn.disconnect" })}
                </Button>
              </div>
            </div>
          }
          content={
            <div>
              <Loading open={loading} fullScreen={false} />
              <div style={{ maxWidth: 700 }}>
                {renderTelegramNotificationConfigItem()}
              </div>
            </div>
          }
        />
      </StyledIntegrationWrapper>
    </>
  );
};

export default IntegrationTelegramNotification;
